import React, { useMemo } from "react";

import {
    ComposableChartLegacy as ComposableChartLegacyComponent,
    type ComposableChartLegacyManifest,
} from "heat-next";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

import { sanitizeAndAlignCharts } from "@/utils/sanitizeTimeline";

function findLayoutColConfiguration(
    gridLayoutData: unknown,
    componentId: string,
): Record<string, unknown> | undefined {
    if (!Array.isArray(gridLayoutData)) return undefined;

    for (const row of gridLayoutData) {
        const cols = (row as { cols?: unknown[] })?.cols;
        if (!Array.isArray(cols)) continue;

        for (const col of cols) {
            const c = col as { component?: string; configuration?: Record<string, unknown> };
            if (c.component === componentId) {
                return c.configuration;
            }
        }
    }

    return undefined;
}

const ComposableChart: React.FC = () => {
    const {
        combinedCustomChartsData,
        isFetchingCombinedCustomCharts,
        isErrorCombinedCustomCharts,
        gridLayoutData,
    } = useSessionDetailsContext();

    const manifest = useMemo((): ComposableChartLegacyManifest | null => {
        const configuration = findLayoutColConfiguration(gridLayoutData, "ComposableChart");
        const embedded = configuration?.composableChartLegacyManifest;
        if (embedded && typeof embedded === "object") {
            return embedded as ComposableChartLegacyManifest;
        }
        return null;
    }, [gridLayoutData]);

    const hasCombinedCustomChartsData = () => {
        return combinedCustomChartsData?.some((entry: { datasets?: { data?: unknown[] }[] }) => {
            return entry?.datasets?.some((dataset) => {
                return Array.isArray(dataset?.data) && dataset.data.length > 0;
            });
        });
    };

    if (isFetchingCombinedCustomCharts) {
        return <ContentLoader height="22.6875rem" />;
    }

    if (isErrorCombinedCustomCharts && !isFetchingCombinedCustomCharts) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="19.6875rem"
                />
            </ComponentWrapper>
        );
    }

    if (
        !combinedCustomChartsData ||
        combinedCustomChartsData.length === 0 ||
        !hasCombinedCustomChartsData()
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:timeline-chart:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="19.6875rem"
                />
            </ComponentWrapper>
        );
    }

    if (!manifest) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:timeline-chart:empty-state:title"
                    description="Composable chart layout manifest is not configured."
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="19.6875rem"
                />
            </ComponentWrapper>
        );
    }

    const sanitizedData = sanitizeAndAlignCharts(combinedCustomChartsData);

    return (
        <ComponentWrapper>
            <ComposableChartLegacyComponent
                manifest={manifest}
                payload={sanitizedData as never}
            />
        </ComponentWrapper>
    );
};

export default ComposableChart;
